/**
 * Authenticated layout route — wraps all /app/* routes.
 *
 * Pattern 5 from RESEARCH.md (hardened): beforeLoad calls ensureValidSession()
 * and throws redirect({ to: '/login' }) when there is no USABLE Cognito session.
 * (Previously used getCurrentUser(), which only decodes cached tokens and admitted
 * stale sessions for deleted users — see debug session google-sso-budget-401.)
 *
 * T-03-01 (PLAN threat model): Route guard prevents unauthenticated access
 * to /app/balance; the API additionally rejects 401 as defense in depth.
 *
 * AUTH-03: Sign out control in top-right, persistent across all /app/* screens.
 * UI-SPEC: "Sign out" label (14px/400 regular), Slate-500, hover Slate-900.
 */
import { createFileRoute, redirect, Outlet, useNavigate, Link, useRouterState } from '@tanstack/react-router';
import { signOut, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ensureValidSession } from '@/lib/auth';
import { LogOut, LogIn, Menu, ArrowLeft, CircleUserRound, Tags, Settings } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useCalendarStore } from '@/features/calendar/calendarStore';
import { useIsAdmin } from '@/features/admin/useIsAdmin';
import { AdminUserPicker } from '@/features/admin/AdminUserPicker';
import { BrandLogo } from '@/components/BrandLogo';
import { isLocalModeActive, setLocalModeActive, LOCAL_MODE_PLATFORM_ENABLED } from '@/lib/localMode';
import { hasLocalDataToMigrate, migrateLocalDataToAccount } from '@/lib/localMigration';
import { ENTRIES_QUERY_KEY } from '@/features/entries/entryApi';
import { CATEGORIES_QUERY_KEY } from '@/features/categories/categoryApi';
import { BALANCE_QUERY_KEY } from '@/features/balance/balanceApi';

/**
 * Module-level guard for the local-to-account migration effect in `AppLayout`
 * below (LOCAL-06, 05.1-04). Declared at MODULE scope, not component state
 * and not a ref: React 19 StrictMode double-invokes effects in development,
 * and `AppLayout` remounts on route changes — either would otherwise fire a
 * second migration attempt mid-flight and duplicate every uploaded entry.
 * Same freeze-at-module-scope reasoning already established for
 * `pageLoadedAsOAuthCallback` in `lib/auth.ts`. Set to `true` before the
 * migration's first `await` and left `true` on success; reset to `false` on
 * failure so the next app launch retries (the local data was deliberately
 * left intact on a failed attempt — see `lib/localMigration.ts`).
 */
let localMigrationAttempted = false;

function AppLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toggleSummary = useCalendarStore((s) => s.toggleSummary);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // (quick-260702-qbr) VIEW-ONLY admin impersonation: the picker only renders for admins.
  const isAdmin = useIsAdmin();
  // The calendar is the app root (/). On the root show the summary hamburger;
  // on any other page (e.g. /categories) show a back arrow to the calendar.
  const onCalendar = pathname === '/';

  // Read directly during render rather than via a store/context/state hook: `beforeLoad`
  // below runs to completion (and activates the local-mode flag) before this component
  // ever renders, and it re-runs on every navigation, so any change to the flag is always
  // accompanied by a re-render of this route tree. Safe to read as a plain function call.
  const localMode = isLocalModeActive();

  // Profile dropdown (Sign out). Closes on outside click.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Profile picture from the signed-in user's attributes (populated for Google sign-ins).
  const [picture, setPicture] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    void (async () => {
      // Prefer the picture claim straight off the ID token (most reliable for federated sign-ins);
      // fall back to fetchUserAttributes. Either way, fall back to the icon on failure.
      try {
        const claim = (await fetchAuthSession()).tokens?.idToken?.payload?.picture;
        if (active && typeof claim === 'string' && claim) {
          setPicture(claim);
          return;
        }
      } catch {
        /* ignore — try attributes next */
      }
      try {
        const attrs = await fetchUserAttributes();
        if (active && attrs.picture) setPicture(attrs.picture);
      } catch {
        /* not signed in or no picture — fall back to the default icon */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  /**
   * LOCAL-06 (05.1-04): the moment a local-mode user has a real session —
   * i.e. they just signed in from local mode — move whatever they built up
   * signed-out into the account, then clear the device store. Mirrors the
   * shape of the profile-picture effect above, including its `active`
   * cleanup flag.
   *
   * Gated on all three conditions, cheapest and most restrictive first:
   *   1. `LOCAL_MODE_PLATFORM_ENABLED` — false on web and Android, so
   *      nothing below can ever execute for the 11 production users there.
   *   2. `!isLocalModeActive()` — a real session exists (`beforeLoad` has
   *      already resolved and set the flag before this component renders).
   *   3. `hasLocalDataToMigrate()` — there is something worth moving.
   */
  useEffect(() => {
    let active = true;
    if (
      LOCAL_MODE_PLATFORM_ENABLED &&
      !isLocalModeActive() &&
      !localMigrationAttempted &&
      hasLocalDataToMigrate()
    ) {
      localMigrationAttempted = true;
      void (async () => {
        try {
          const result = await migrateLocalDataToAccount();
          if (!active) return;
          const entryWord = result.entries === 1 ? 'entry' : 'entries';
          const balancePart = result.balanceMigrated ? ' and your starting balance' : '';
          toast.success(`Moved ${result.entries} ${entryWord}${balancePart} into your account.`);
          const migratedKeys = [ENTRIES_QUERY_KEY, CATEGORIES_QUERY_KEY, BALANCE_QUERY_KEY];
          await queryClient.invalidateQueries({
            predicate: (query) =>
              migratedKeys.some(
                (key) =>
                  key.length === query.queryKey.length &&
                  key.every((part, i) => part === query.queryKey[i])
              ),
          });
        } catch (err) {
          if (!active) return;
          // Local data was deliberately left intact on failure (see
          // lib/localMigration.ts) — reset the guard so the next launch
          // retries automatically instead of staying permanently stuck.
          localMigrationAttempted = false;
          console.warn('[localMigration] migration failed, will retry on next launch', err);
          toast.error(
            "Couldn't sync your local data to your account yet. It's still on this device and will be retried automatically."
          );
        }
      })();
    }
    return () => {
      active = false;
    };
  }, [queryClient]);

  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  // CAP-05: document-level Escape listener (mirrors the outside-click handler above) so
  // the Android back-button handler's synthetic Escape dispatch (nativeBack.ts) can close
  // this non-Radix profile dropdown too.
  useEffect(() => {
    if (!menuOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  async function handleSignOut() {
    setMenuOpen(false);
    // Federated (Google) users get an OAuth logout redirect whose target Amplify selects by
    // window.location.origin + pathname. Only the root "/" reliably matches a registered logout
    // URL (the "<origin>/login" entries startsWith "<origin>/"), so move there first when needed.
    if (pathname !== '/') {
      await navigate({ to: '/' });
    }
    await signOut();
    // AUTH-03: redirect to the sign-in page after sign out (email/password users don't redirect).
    // Decision 2 (DECISIONS.md, commit a662dba): on iOS, sign-out lands the user back on the
    // calendar in a fresh local-mode session instead of the sign-in wall — the app must stay
    // usable without an account there. Web and Android are unchanged and still land on /login.
    if (LOCAL_MODE_PLATFORM_ENABLED) {
      void navigate({ to: '/' });
    } else {
      void navigate({ to: '/login' });
    }
  }

  const menuItem =
    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50';

  return (
    <div className="min-h-[100dvh] bg-slate-50 overflow-x-clip">
      {/* Persistent sticky header — hamburger + brand (left), profile menu (right).
          CAP-06: top safe-area padding so the header content clears the status bar /
          Dynamic Island on native instead of sitting underneath it. Variable-first
          fallback chain (matches the FAB/BudgetPanel bottom pattern): the injected
          --safe-area-inset-top is more reliable than env() on Android WebView <140;
          env() covers iOS and plain web (where the custom property is unset, so it
          falls through). No-op (0px) on devices/browsers without a safe area. */}
      <header
        className="sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur"
        style={{ paddingTop: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))' }}
      >
        <div className="flex h-16 items-center justify-between pl-2 pr-3 sm:pr-4">
          {/* Left: hamburger (calendar/root) OR back arrow (other pages) + brand */}
          <div className="flex items-center gap-2 min-w-0">
            {onCalendar ? (
              <button
                type="button"
                onClick={toggleSummary}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                aria-label="Toggle summary"
              >
                <Menu className="h-5 w-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => { void navigate({ to: '/' }); }}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                aria-label="Back to calendar"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <Link to="/" className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500 text-white">
                <BrandLogo className="h-5 w-5" />
              </span>
              <span className="truncate text-base font-semibold tracking-tight text-slate-900">Broke or Not</span>
            </Link>
          </div>

          {/* Right: admin user picker (admin-only) + profile menu with Categories + Sign out */}
          <div className="flex items-center gap-3 min-w-0">
          {isAdmin && <AdminUserPicker isAdmin={isAdmin} />}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
              aria-label="Account menu"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              {picture ? (
                <img
                  src={picture}
                  alt="Profile"
                  referrerPolicy="no-referrer"
                  className="h-8 w-8 rounded-full object-cover"
                  onError={() => setPicture(null)}
                />
              ) : (
                <CircleUserRound className="h-6 w-6" />
              )}
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-44 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              >
                <Link
                  to="/categories"
                  role="menuitem"
                  className={menuItem}
                  onClick={() => setMenuOpen(false)}
                >
                  <Tags className="h-4 w-4 text-slate-400" />
                  Categories
                </Link>
                <Link
                  to="/settings"
                  role="menuitem"
                  className={menuItem}
                  onClick={() => setMenuOpen(false)}
                >
                  <Settings className="h-4 w-4 text-slate-400" />
                  Settings
                </Link>
                {localMode ? (
                  <Link
                    to="/login"
                    role="menuitem"
                    className={menuItem}
                    onClick={() => setMenuOpen(false)}
                  >
                    <LogIn className="h-4 w-4 text-slate-400" />
                    Sign in to sync
                  </Link>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { void handleSignOut(); }}
                    className={menuItem}
                  >
                    <LogOut className="h-4 w-4 text-slate-400" />
                    Sign out
                  </button>
                )}
              </div>
            )}
          </div>
          </div>
        </div>
      </header>

      {/* Render the matched child route */}
      <main>
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createFileRoute('/_app')({
  /**
   * Auth guard (T-03-01): require a session that can actually produce a valid
   * Cognito token before rendering any /app/* route.
   *
   * NOTE: we deliberately do NOT use getCurrentUser() here — in Amplify v6 it only
   * DECODES cached tokens and never validates/refreshes them, so it admits a
   * half-authenticated shell when the browser holds stale tokens for a deleted
   * user (the google-sso-budget-401 bug: the calendar shell rendered but every
   * data query failed). ensureValidSession() forces a refresh and clears stale
   * tokens, so the guard only passes when the session is genuinely usable.
   *
   * Local mode (LOCAL-01, App Store submission d86a49f2, Guideline 5.1.1(v)):
   * Apple's third rejection asked for the sign-in wall to be REMOVED on iOS, not
   * softened, so an unauthenticated iOS user must be admitted rather than turned
   * away. The local-mode activation setter is invoked here with `!valid` — the
   * ONLY call site in the codebase — so the flag is set before AppLayout renders
   * or any TanStack query fires. It is ANDed against `LOCAL_MODE_PLATFORM_ENABLED` inside
   * `lib/localMode.ts`, which is `true` only inside the native iOS shell, so this
   * call is a no-op everywhere else. When there is no valid session AND local mode
   * is not available for this platform, the original redirect fires completely
   * unchanged — web and Android behaviour is untouched by this feature.
   */
  beforeLoad: async () => {
    const valid = await ensureValidSession();
    setLocalModeActive(!valid);
    if (!valid && !LOCAL_MODE_PLATFORM_ENABLED) {
      throw redirect({ to: '/login' });
    }
    // Either the session is valid, or local mode is available for this platform:
    // fall through and render the app (in local mode on the second branch).
  },
  component: AppLayout,
});
